import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import DevisPage from "@/pages/DevisPage";
import DevisFormPage from "@/pages/DevisFormPage";
import CommandesPage from "@/pages/CommandesPage";
import ClientsPage from "@/pages/ClientsPage";
import SettingsPage from "@/pages/SettingsPage";
import PortalPage from "@/pages/PortalPage";
import FacturesPage from "@/pages/FacturesPage";
import MailPage from "@/pages/MailPage";
import InterventionsPage from "@/pages/InterventionsPage";
import UsersPage from "@/pages/UsersPage";
import EspaceClientPage from "@/pages/EspaceClientPage";
import ComptabilitePage from "@/pages/ComptabilitePage";
import TemplatesPage from "@/pages/TemplatesPage";
import WebmasterPage from "@/pages/WebmasterPage";
import AppLayout from "@/components/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

    if (sessionId) {
      fetch(`${API}/auth/session?session_id=${sessionId}`, { credentials: "include" })
        .then(r => {
          if (!r.ok) throw new Error("Session invalide");
          return r.json();
        })
        .then(userData => {
          // Set user in auth context so ProtectedRoute allows access
          setUser(userData);
          const dest = (userData.role === "admin" || userData.role === "webmaster") ? "/dashboard" : "/espace-client";
          navigate(dest, { replace: true });
        })
        .catch(() => { navigate("/login", { replace: true }); });
    } else {
      navigate("/login", { replace: true });
    }
  }, [navigate, setUser]);

  return (
    <div className="flex h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-600 text-sm">Connexion en cours...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <AppLayout>{children}</AppLayout>
  );
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role !== "admin" && user.role !== "webmaster") {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H8m13-6a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Accès restreint</h2>
            <p className="text-sm text-slate-500">Vous n'avez pas les droits pour accéder à cette section.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>{children}</AppLayout>
  );
}

function DefaultRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={(user.role === "admin" || user.role === "webmaster") ? "/dashboard" : "/espace-client"} replace />;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/portal/:token" element={<PortalPage />} />
      <Route path="/dashboard" element={<AdminRoute><DashboardPage /></AdminRoute>} />
      <Route path="/devis" element={<AdminRoute><DevisPage /></AdminRoute>} />
      <Route path="/devis/new" element={<AdminRoute><DevisFormPage /></AdminRoute>} />
      <Route path="/devis/:id/edit" element={<AdminRoute><DevisFormPage /></AdminRoute>} />
      <Route path="/commandes" element={<AdminRoute><CommandesPage /></AdminRoute>} />
      <Route path="/factures" element={<AdminRoute><FacturesPage /></AdminRoute>} />
      <Route path="/clients" element={<AdminRoute><ClientsPage /></AdminRoute>} />
      <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
      <Route path="/messagerie" element={<AdminRoute><MailPage /></AdminRoute>} />
      <Route path="/interventions" element={<AdminRoute><InterventionsPage /></AdminRoute>} />
      <Route path="/utilisateurs" element={<AdminRoute><UsersPage /></AdminRoute>} />
      <Route path="/comptabilite" element={<AdminRoute><ComptabilitePage /></AdminRoute>} />
      <Route path="/modeles" element={<AdminRoute><TemplatesPage /></AdminRoute>} />
      <Route path="/webmaster" element={<AdminRoute><WebmasterPage /></AdminRoute>} />
      <Route path="/espace-client" element={<ProtectedRoute><EspaceClientPage /></ProtectedRoute>} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
